import { FC, useState } from "react";
import {
  PackageCheck,
  Archive,
  Printer,
  Check,
  ShoppingBag,
  X,
  Loader2,
} from "lucide-react";

import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { Colors } from "../../../configs";
import { formatCurrency } from "../../../util";
import useTrayPrinter from "../../../hooks/useTrayPrinter";
import { useTranslation } from "../../../util/translation-provider";
import { formatUSDate, formatUS12HourTime } from "../../../util/common-functions";
import { isDeferredPayment } from "../../../util/print-formatting";
import { PaymentTypeEnum } from "../../../typings/enum/paymentTypes";
import { PrintCopyChoice } from "../../../components/pos/payment-modal/CashChangeModal";
import {
  ITrayPrintCustomizationLine,
  ITrayPrintItem,
  ITrayPrintRequest,
} from "../../../typings/interfaces/print/ticket-print-request";

export interface IPickupReceiptItem {
  description: string;
  name: string;
  qty: number;
  // Discount- and prior-payment-aware net price for this item (post the
  // bill's pro-rata discount share and any already-paid amount) - this is
  // what actually reconciles with what was charged/is still owed, NOT the
  // raw gross qty*unitPrice+extras total.
  price: number;
  // Raw pre-discount gross total for this item - kept alongside `price`
  // purely so the receipt can show an accurate discount figure instead of a
  // hardcoded 0; never used for the total/balance maths itself.
  grossPrice: number;
  // Free-text note a cashier attached to this specific item.
  note?: string;
  // Upcharges/spots/damages/release notes/alterations attached to this item,
  // grouped by section, each printed as its own "+"-prefixed line under it.
  customizations?: ITrayPrintCustomizationLine[];
}

export interface IPickupReceiptBillSummary {
  billId: string;
  // Items resolved into THIS pickup transaction - "leaving today".
  leavingItems: IPickupReceiptItem[];
  // Items still on the bill that were not selected this time (and weren't
  // already picked up in an earlier transaction) - "remaining in storage".
  remainingItems: IPickupReceiptItem[];
  amountChargedThisTransaction: number;
  // Raw cash actually tendered for THIS pickup transaction (before capping
  // to what's owed) - only set when this was a single-bill CASH pickup;
  // PickupModal.tsx's own cashTenderedRef/cashBillRef track the whole
  // transaction's combined cash, not any one bill's share, so a multi-bill
  // pickup (one payment split across several bills) leaves this undefined
  // and falls back to amountChargedThisTransaction (see buildPickupReceiptPrintRequest).
  cashTendered?: number;
  // The bill's assigned pin tag - carried through so the pickup receipt can
  // print it the same way check-in's receipt does.
  pinTagColor?: string;
  pinTagTags?: string[];
  pinTagAlias?: string;
  // The bill's originally scheduled pickup date - only used to print a "Due
  // Next" row when this is a partial pickup (remainingItems is non-empty).
  scheduledPickupDate?: Date;
  // Bill-level cashier notes, carried through the same way.
  notes?: string[];
}

interface IPickupReceiptModalProps {
  open: boolean;
  onDone: () => void;
  bills: IPickupReceiptBillSummary[];
  customerName?: string;
  customerId?: string;
  // Single-letter customer type (R/B/E) and on-file payment preference
  // (M/C), printed next to `customerName` the same way check-in's does.
  customerStatus?: string;
  paymentStatus?: string;
  // Printed on the ticket the same way check-in's does - a store copy with no
  // cashier on it can't be traced back to whoever handed the garments over.
  cashierName?: string;
  cashierId?: string;
  paidBy?: string;
}

// App's established "pending/deferred" amber - the same accent
// CashChangeModal already uses for its LATER/pay-later highlight, reused
// here for "Remaining in Storage" so both screens speak the same color
// language instead of this file inventing its own amber.
const AMBER = "#D89216";
// App's established green-on-tint text, already used by CashChangeModal's
// own highlight banner (paired with `${Colors.PRIMARY}14` as the tint).
const GREEN_TEXT_ON_TINT = "#1B6B2A";

const PRINT_OPTIONS: { key: PrintCopyChoice; label: string }[] = [
  { key: "both", label: "Print Both" },
  { key: "storeCopy", label: "Store Copy" },
  { key: "receipt", label: "Customer Copy" },
];

// Builds a tray-printer payload for one bill's itemized pickup receipt -
// reuses the existing ITrayPrintRequest shape (see util/ticket-print.ts and
// payment-modal.tsx's buildPrintRequestForBill) rather than inventing a new
// wire format, labelling each line item's section directly in its
// description so the physical receipt stays readable on today's printer
// without requiring any tray-app schema change. `copyType` mirrors
// payment-modal.tsx's own store-copy/customer-copy split - same content,
// just tagged for whichever physical copy the tray app is printing.
interface IPickupPrintContext {
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  customerName?: string;
  customerId?: string;
  customerStatus?: string;
  paymentStatus?: string;
  cashierName?: string;
  cashierId?: string;
  paidBy?: string;
}

const buildPickupReceiptPrintRequest = (
  bill: IPickupReceiptBillSummary,
  copyType: "receipt" | "storeCopy",
  context: IPickupPrintContext
): ITrayPrintRequest => {
  const now = new Date();
  // Only a partial pickup actually needs the "- Leaving Today"/"- Remain in
  // Store" tags to distinguish the two groups - a full pickup has nothing
  // remaining, so every item is leaving and the receipt should read like a
  // regular, untagged item list.
  const isPartial = bill.remainingItems.length > 0;
  const items: ITrayPrintItem[] = [
    ...bill.leavingItems.map((item) => ({
      qty: item.qty,
      description: isPartial ? `${item.description} - Leaving Today` : item.description,
      price: item.price,
      note: item.note,
      customizations: item.customizations,
    })),
    ...bill.remainingItems.map((item) => ({
      qty: item.qty,
      description: `${item.description} - Remain in Store`,
      price: item.price,
      note: item.note,
      customizations: item.customizations,
    })),
  ];
  // Net total for what's actually leaving today - `item.price` is discount-
  // AND prior-payment-aware (see IPickupReceiptItem's own doc comment), so
  // this doubles as both the printed Total (Subtotal - Discount, per that
  // same pre-existing "Discount" figure below, which folds any already-paid
  // amount in alongside the real discount) and the Balance calc, which is
  // what reconciles with what was charged/is still owed for THIS
  // transaction, scoped to the leaving items only (the "remaining in
  // storage" side is shown separately below and isn't part of this
  // transaction's charge).
  const leavingTotal = bill.leavingItems.reduce((acc, i) => acc + i.price, 0);
  // Gross (pre-discount, pre-prior-payment) subtotal for the leaving items -
  // printed as Subtotal, with Discount (below) broken out as its own line.
  const grossLeavingTotal = bill.leavingItems.reduce(
    (acc, i) => acc + i.grossPrice,
    0
  );
  // NOTE: folds any already-paid amount in alongside the real discount
  // (pre-existing approximation - see IPickupReceiptItem.price's own
  // comment - not something this change introduces or attempts to fix).
  const leavingDiscount = Math.max(0, grossLeavingTotal - leavingTotal);
  // amountChargedThisTransaction is what was actually RECORDED/applied
  // against the bill (capped at what's owed - a payment record can never
  // exceed the due amount), so for a CASH pickup where the customer
  // tendered more than was due and got change back, it silently equals
  // the amount due, hiding what was actually handed over. Prefer the raw
  // tendered amount (bill.cashTendered, set by PickupModal.tsx only for a
  // single-bill CASH pickup - see its own doc comment) when available,
  // mirroring check-in's identical fix in payment-modal.tsx.
  const isCashWithKnownTender =
    context.paidBy === PaymentTypeEnum.CASH && bill.cashTendered != null;
  // LATER/MONTHLY submit the full bill amount as the "payment" purely to
  // let the backend track it as a deferred NOT_CLEARED charge - nothing was
  // actually collected, so treating amountChargedThisTransaction as
  // genuinely paid printed every deferred pickup receipt's balance as $0.
  const paid = isDeferredPayment(context.paidBy)
    ? 0
    : isCashWithKnownTender
      ? bill.cashTendered!
      : bill.amountChargedThisTransaction;

  return {
    copyType,
    invoice: bill.billId,
    // Every pickup prints from here now, so the heading has to say which kind
    // this was rather than always claiming a partial one.
    department:
      bill.remainingItems.length > 0 ? "PARTIAL PICKUP" : "PICKUP",
    dropOff: `${formatUSDate(now)} · ${formatUS12HourTime(now)}`,
    // This receipt documents an active pickup happening right now, so the
    // top-left row always reads "Picked Up" with the actual time - see
    // Print Issues.pdf #4.
    pickupLabel: "Picked Up",
    pickup: formatUSDate(now),
    pickupAfter: formatUS12HourTime(now),
    // Only a partial pickup (something still remains) needs a "Due Next"
    // row - a full pickup has nothing left to be due.
    dueNext:
      bill.remainingItems.length > 0 && bill.scheduledPickupDate
        ? formatUSDate(bill.scheduledPickupDate)
        : undefined,
    dueNextAfter:
      bill.remainingItems.length > 0 && bill.scheduledPickupDate
        ? formatUS12HourTime(bill.scheduledPickupDate)
        : undefined,
    customer: context.customerName ?? "",
    customerId: context.customerId ?? "",
    customerStatus: context.customerStatus,
    paymentStatus: context.paymentStatus,
    cashier: context.cashierName ?? "",
    cashierId: context.cashierId ?? "",
    storeName: context.companyName,
    storeSubtitle: "Certified Environmental Dry Cleaner",
    storeAddress: context.companyAddress,
    storePhone: context.companyPhone,
    storeWebsite: "www.prestovalet.net",
    items,
    subtotal: grossLeavingTotal,
    discount: leavingDiscount,
    // Printed Total is the post-discount amount (Subtotal - Discount, both
    // still shown as their own rows above it); Balance reconciles that
    // against what's actually been paid.
    total: leavingTotal,
    notes: bill.notes,
    paidBy: context.paidBy ?? "",
    paid,
    // Absolute difference so an overpaid CASH pickup (paid > total, only
    // possible in the isCashWithKnownTender case above) prints the change
    // given back instead of clamping to $0, matching check-in's identical
    // Balance convention.
    balance: Math.abs(leavingTotal - paid),
    pinTagColor: bill.pinTagColor,
    pinTagTags: bill.pinTagTags ?? [],
    pinTagAlias: bill.pinTagAlias,
  };
};

const ItemRow: FC<{ item: IPickupReceiptItem }> = ({ item }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 text-[length:var(--fs-base)]">
    <div className="min-w-0">
      <div className="truncate font-semibold text-[#4D4D4D]">{item.name}</div>
      {item.description !== item.name && (
        <div className="truncate text-[length:var(--fs-xs)] text-[#4D4D4D]/60">
          {item.description}
        </div>
      )}
    </div>
    <div className="flex flex-none items-center gap-3">
      <span className="whitespace-nowrap text-[length:var(--fs-xs)] text-[#4D4D4D]/60">
        ×{item.qty}
      </span>
      <span className="min-w-[60px] whitespace-nowrap text-right font-semibold text-[#4D4D4D]">
        {formatCurrency({ amount: item.price, prefix: "$" })}
      </span>
    </div>
  </div>
);

const BillReceiptSection: FC<{ bill: IPickupReceiptBillSummary }> = ({
  bill,
}) => {
  const leavingTotal = bill.leavingItems.reduce((acc, i) => acc + i.price, 0);
  const remainingTotal = bill.remainingItems.reduce(
    (acc, i) => acc + i.price,
    0
  );

  return (
    <div className="mb-4">
      {/* Invoice identity row - same icon-badge + name/sub layout language
          CashChangeModal already uses for its "payment method" row. */}
      <div className="mb-3 flex items-center gap-3">
        <div
          className="flex h-[3.5rem] w-[3.5rem] shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${Colors.PRIMARY}21`, color: Colors.PRIMARY }}
        >
          <ShoppingBag size={24} />
        </div>
        <div className="min-w-0">
          <div className="truncate text-[length:var(--fs-md)] font-bold text-[#4D4D4D]">
            Invoice {bill.billId}
          </div>
          <div className="mt-0.5 text-[length:var(--fs-xs)] text-[#4D4D4D]/75">
            {bill.leavingItems.length} piece{bill.leavingItems.length === 1 ? "" : "s"} leaving today
            {" · "}
            {bill.remainingItems.length} piece{bill.remainingItems.length === 1 ? "" : "s"} still in storage
          </div>
        </div>
      </div>

      <div
        className="rounded-xl p-4"
        style={{ backgroundColor: Colors.BACKSHADE_GRAY }}
      >
        {/* divide-[#D9D9D9] mirrors Colors.ACCENT_GRAY - Tailwind's JIT
            needs the literal arbitrary-value class, not an interpolated
            constant, to compile the child-border rule `divide-x` relies on. */}
        <div className="grid grid-cols-2 divide-x divide-[#D9D9D9]">
          <div className="pr-4">
            <div
              className="mb-2 flex items-center gap-1.5 text-[length:var(--fs-xs)] font-bold uppercase tracking-wide"
              style={{ color: Colors.PRIMARY }}
            >
              <PackageCheck size={14} />
              Leaving Today ({bill.leavingItems.length})
            </div>
            {bill.leavingItems.length === 0 ? (
              <div className="text-[length:var(--fs-xs)] text-[#4D4D4D]/50">
                Nothing leaving on this invoice
              </div>
            ) : (
              <div className="divide-y divide-[#D9D9D9]/60">
                {bill.leavingItems.map((item, idx) => (
                  <ItemRow key={`${bill.billId}-leaving-${idx}`} item={item} />
                ))}
              </div>
            )}
            <div
              className="mt-2 flex items-center justify-between border-t pt-2 text-[length:var(--fs-base)] font-bold"
              style={{ borderColor: `${Colors.PRIMARY}33`, color: Colors.PRIMARY }}
            >
              <span>Subtotal</span>
              <span>{formatCurrency({ amount: leavingTotal, prefix: "$" })}</span>
            </div>
          </div>

          <div className="pl-4">
            <div
              className="mb-2 flex items-center gap-1.5 text-[length:var(--fs-xs)] font-bold uppercase tracking-wide"
              style={{ color: AMBER }}
            >
              <Archive size={14} />
              Remain in Store ({bill.remainingItems.length})
            </div>
            {bill.remainingItems.length === 0 ? (
              <div className="text-[length:var(--fs-xs)] text-[#4D4D4D]/50">
                Nothing left in storage on this invoice
              </div>
            ) : (
              <div className="divide-y divide-[#D9D9D9]/60">
                {bill.remainingItems.map((item, idx) => (
                  <ItemRow key={`${bill.billId}-remaining-${idx}`} item={item} />
                ))}
              </div>
            )}
            <div
              className="mt-2 flex items-center justify-between border-t pt-2 text-[length:var(--fs-base)] font-bold"
              style={{ borderColor: `${AMBER}33`, color: AMBER }}
            >
              <span>Still Open</span>
              <span>
                {formatCurrency({ amount: remainingTotal, prefix: "$" })}
              </span>
            </div>
          </div>
        </div>

        <div
          className="mt-3 flex items-center justify-between gap-4 rounded-lg px-3 py-3"
          style={{ backgroundColor: `${Colors.PRIMARY}14` }}
        >
          <span
            className="text-[length:var(--fs-base)] font-bold"
            style={{ color: GREEN_TEXT_ON_TINT }}
          >
            Charged This Visit
          </span>
          <span
            className="text-[length:var(--fs-md)] font-bold"
            style={{ color: GREEN_TEXT_ON_TINT }}
          >
            {formatCurrency({
              amount: bill.amountChargedThisTransaction,
              prefix: "$",
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

// On-screen (and, via the Print button, printed) itemized receipt for a
// partial pickup transaction - separates exactly which items are "leaving
// today" from what's "remaining in storage" per bill, following the same
// two-column picked/remaining convention already used for pickup events in
// invoice-history-modal.tsx. Visual language (success badge, method/detail
// panel, highlight banner, 3-way print-copy picker) mirrors CashChangeModal
// - the app's own established "transaction complete" confirmation pattern -
// rather than a one-off design for this screen.
const PickupReceiptModal: FC<IPickupReceiptModalProps> = ({
  open,
  onDone,
  bills,
  customerName,
  customerId,
  customerStatus,
  paymentStatus,
  cashierName,
  cashierId,
  paidBy,
}) => {
  const { printTicket } = useTrayPrinter();
  const translation = useTranslation();
  const [printingCopy, setPrintingCopy] = useState<PrintCopyChoice | null>(null);
  const [printFeedback, setPrintFeedback] = useState<{
    copy: PrintCopyChoice;
    success: boolean;
    message?: string;
  } | null>(null);

  const handlePrintCopySelection = async (copy: PrintCopyChoice) => {
    if (printingCopy || bills.length === 0) return;

    const context: IPickupPrintContext = {
      companyName: translation.companyName,
      companyAddress: translation.companyAddress,
      companyPhone: translation.companyPhone,
      customerName,
      customerId,
      customerStatus,
      paymentStatus,
      cashierName,
      cashierId,
      paidBy,
    };
    const allRequests = bills.flatMap((bill) => [
      buildPickupReceiptPrintRequest(bill, "storeCopy", context),
      buildPickupReceiptPrintRequest(bill, "receipt", context),
    ]);
    const filtered =
      copy === "both" ? allRequests : allRequests.filter((r) => r.copyType === copy);

    if (filtered.length === 0) return;

    setPrintingCopy(copy);
    setPrintFeedback(null);

    let failures = 0;
    for (const req of filtered) {
      const ok = await printTicket(req);
      if (!ok) failures++;
    }
    setPrintingCopy(null);
    setPrintFeedback({
      copy,
      success: failures === 0,
      message:
        failures === 0
          ? undefined
          : failures === filtered.length
            ? "Could not reach the printer. Make sure Presto Tray is running on this computer."
            : `${failures} of ${filtered.length} copies failed to print.`,
    });
  };

  const totalLeavingPieces = bills.reduce(
    (acc, b) => acc + b.leavingItems.reduce((s, i) => s + i.qty, 0),
    0
  );
  const totalRemainingPieces = bills.reduce(
    (acc, b) => acc + b.remainingItems.reduce((s, i) => s + i.qty, 0),
    0
  );

  return (
    <CustomModal
      title="Pickup Receipt"
      openModal={open}
      onClose={onDone}
      mode="popup"
      hideCloseButton
      width="720px"
      height="auto"
      sx={{ maxHeight: "90vh", overflow: "hidden", borderRadius: "1.5rem" }}
    >
      <div className="flex max-h-[80vh] flex-col">
        <div className="relative pb-1 pt-1">
          <button
            type="button"
            onClick={onDone}
            aria-label="Close"
            className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center rounded-full text-[#7C7C7C] transition hover:bg-[#F3F3F3]"
          >
            <X size={20} />
          </button>

          <h2 className="text-center text-[length:var(--fs-lg)] font-extrabold text-[#4D4D4D]">
            Partial Pickup Complete
          </h2>

          <div className="mt-3 flex justify-center">
            <div
              className="flex h-[3.5rem] w-[3.5rem] items-center justify-center rounded-full text-white"
              style={{ backgroundColor: Colors.PRIMARY }}
            >
              <Check size={28} strokeWidth={2.6} />
            </div>
          </div>
          <div
            className="mt-2 text-center text-[length:var(--fs-md)] font-bold"
            style={{ color: Colors.PRIMARY }}
          >
            Pickup Recorded
          </div>

          <p className="mt-2 text-center text-[length:var(--fs-base)] text-[#4D4D4D]/60">
            {totalLeavingPieces} piece{totalLeavingPieces === 1 ? "" : "s"}{" "}
            leaving today · {totalRemainingPieces} piece
            {totalRemainingPieces === 1 ? "" : "s"} still in storage
          </p>
        </div>

        <div className="mt-3 flex-1 overflow-y-auto px-1 pb-1">
          {bills.map((bill) => (
            <BillReceiptSection key={bill.billId} bill={bill} />
          ))}
        </div>

        <div className="mb-2 text-center text-[length:var(--fs-base)] font-semibold text-[#4D4D4D]">
          Print Receipt
        </div>
        <div className="mb-2 grid grid-cols-3 gap-2">
          {PRINT_OPTIONS.map((opt) => {
            const isThisPrinting = printingCopy === opt.key;
            const disabled = !!printingCopy;
            return (
              <button
                key={opt.key}
                type="button"
                disabled={disabled}
                onClick={() => handlePrintCopySelection(opt.key)}
                className="flex h-[3.25rem] items-center justify-center gap-2 rounded-xl border px-3 text-[length:var(--fs-2xs)] font-bold transition-all duration-150 disabled:cursor-not-allowed enabled:hover:-translate-y-px"
                style={{
                  borderColor: Colors.SECONDARY,
                  color: "white",
                  background: `linear-gradient(180deg, ${Colors.SECONDARY} 0%, #0568A7 100%)`,
                  boxShadow: "0 8px 18px rgba(0,121,194,0.22)",
                  opacity: disabled && !isThisPrinting ? 0.5 : 1,
                }}
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: "rgba(255,255,255,0.16)", color: "white" }}
                >
                  {isThisPrinting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Printer size={16} />
                  )}
                </span>
                <span className="whitespace-nowrap tracking-tight">
                  {isThisPrinting ? "Printing…" : opt.label}
                </span>
              </button>
            );
          })}
        </div>

        {printFeedback && (
          <div
            className={`mb-3 rounded-lg px-3 py-2 text-center text-[length:var(--fs-xs)] font-semibold ${
              printFeedback.success
                ? "bg-[#018752]/10"
                : "bg-[#930000]/10"
            }`}
            style={{ color: printFeedback.success ? GREEN_TEXT_ON_TINT : Colors.BROWN }}
          >
            {printFeedback.success
              ? `${PRINT_OPTIONS.find((o) => o.key === printFeedback.copy)?.label ?? "Receipt"} sent to the printer.`
              : printFeedback.message}
          </div>
        )}

        <button
          type="button"
          onClick={onDone}
          className="h-[3.2rem] w-full rounded-xl text-[length:var(--fs-base)] font-bold text-white transition hover:brightness-95"
          style={{ backgroundColor: Colors.PRIMARY }}
        >
          Done
        </button>
      </div>
    </CustomModal>
  );
};

export default PickupReceiptModal;
